import { z } from "zod";
import { AUTH_API_BASE } from "./auth-api-base";
import { readApiResponse } from "./app-error";
import { boundedRequest } from "./bounded-request";
import { createPeopleClient, type PeopleClient } from "./rpc/people";

const roleSchema = z.enum(["owner", "admin", "member"]);
export type MembershipRole = z.infer<typeof roleSchema>;

const dateSchema = z.iso.datetime().transform((value) => new Date(value));

const memberSchema = z.object({
  /** The `member` row, which a later change to this person's membership names. */
  id: z.string().min(1),
  /** The user's ID, as a connection's `ownerUserId` holds it. "" means an older identity service did not send it: treat it as unknown. */
  userId: z.string().default(""),
  name: z.string(),
  email: z.string().min(1),
  role: roleSchema,
  /** Null for someone who has never had a session. Coarse: the service refreshes it daily. */
  lastActiveAt: dateSchema.nullable(),
  /** The service's own mark of the caller's row, never worked out here from an address. */
  you: z.boolean(),
});
const invitationSchema = z.object({
  /** An invitation has no id here: that is the credential in its link. The address names it. */
  email: z.string().min(1),
  role: roleSchema,
  invitedBy: z.string().nullable(),
  createdAt: dateSchema,
  expiresAt: dateSchema,
  status: z.enum(["pending", "expired"]),
  /** Whether the email went out. `unknown` when no attempt was written down. */
  delivery: z.enum(["sent", "failed", "unknown"]).default("unknown"),
});
const requestSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  email: z.string().min(1),
  requestedAt: dateSchema,
});

export type PersonMember = z.infer<typeof memberSchema>;
export type PersonInvitation = z.infer<typeof invitationSchema>;
export type PersonRequest = z.infer<typeof requestSchema>;

/**
 * One read, three bands. The service leaves invitations and requests out for anyone but an owner or
 * admin; here they read as empty lists so the bands are one shape, and `canManage` is what says
 * whether empty means "none" or "not yours to see".
 */
const peopleSchema = z
  .object({
    viewer: z.object({ role: roleSchema, canManage: z.boolean() }),
    members: z.array(memberSchema),
    invitations: z.array(invitationSchema).optional(),
    requests: z.array(requestSchema).optional(),
  })
  .transform((read) => ({
    viewer: read.viewer,
    members: read.members,
    invitations: read.invitations ?? [],
    requests: read.requests ?? [],
  }));
export type People = z.infer<typeof peopleSchema>;

export const peopleClient = createPeopleClient(AUTH_API_BASE);

export async function fetchPeople(
  signal: AbortSignal,
  timeoutMs = 10000,
  client: PeopleClient = peopleClient,
): Promise<People> {
  return boundedRequest(
    async (requestSignal) =>
      readApiResponse(
        await client.people.$get(undefined, { init: { signal: requestSignal } }),
        peopleSchema,
      ),
    signal,
    timeoutMs,
  );
}

/** Which group the pill switch shows. */
export type PeopleFilter = "all" | "members" | "invited" | "requests";

/** What the page draws: the rows of each band after the search, and the counts taken from them. */
export type PeopleView = {
  requests: readonly PersonRequest[];
  members: readonly PersonMember[];
  invitations: readonly PersonInvitation[];
  counts: { all: number; members: number; invited: number; requests: number };
};

/** Every word typed must appear in the name or the address, in any case. */
function matcher(query: string) {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  return (...fields: readonly string[]) => {
    const haystack = fields.join(" ").toLowerCase();
    return terms.every((term) => haystack.includes(term));
  };
}

/**
 * The search applied to every band once, and the counts taken from what is left, so a pill, a band
 * heading and the rows under it cannot disagree. The filter only chooses which bands are drawn: the
 * counts ignore it, which is what lets a pill say how many are behind it before it is pressed.
 */
export function viewPeople(people: People, query: string, filter: PeopleFilter): PeopleView {
  const matches = matcher(query);
  const requests = people.requests.filter((row) => matches(row.name, row.email));
  const members = people.members.filter((row) => matches(row.name, row.email));
  const invitations = people.invitations.filter((row) => matches(row.email));
  return {
    requests: filter === "all" || filter === "requests" ? requests : [],
    members: filter === "all" || filter === "members" ? members : [],
    invitations: filter === "all" || filter === "invited" ? invitations : [],
    counts: {
      all: requests.length + members.length + invitations.length,
      members: members.length,
      invited: invitations.length,
      requests: requests.length,
    },
  };
}

/**
 * The two lines General's People group says, counted from the same read as the Members page. The
 * second is for owners and admins alone: a plain member's answer carries no invitations or
 * requests, and a line of zeros would tell them something untrue.
 */
export function peopleSummary(people: People): { members: string; waiting: string | null } {
  const members = plural(people.members.length, "member");
  if (!people.viewer.canManage) return { members, waiting: null };
  const out = people.invitations.length;
  const asking = people.requests.length;
  if (out === 0 && asking === 0) {
    return { members, waiting: "No invitations out, and nobody waiting to be let in" };
  }
  return {
    members,
    waiting: `${plural(out, "invitation")} out, ${asking === 1 ? "1 person" : `${String(asking)} people`} waiting for you to let them in`,
  };
}

/** What the sidebar badge counts: people waiting on a decision. Zero until the read is in. */
export function waitingRequests(people: People | undefined): number {
  return people?.requests.length ?? 0;
}

const ADDRESS = z.email();

/** A whole address, typed in the search box: the only search that offers to invite. */
export function asAddress(query: string): string | null {
  const typed = query.trim();
  return ADDRESS.safeParse(typed).success ? typed : null;
}

/** Whether anyone in any band already holds this address, whatever the filter shows. */
export function holdsAddress(people: People, address: string): boolean {
  const wanted = address.toLowerCase();
  return [...people.members, ...people.invitations, ...people.requests].some(
    (row) => row.email.toLowerCase() === wanted,
  );
}

export function roleName(role: MembershipRole): string {
  return role === "owner" ? "Owner" : role === "admin" ? "Admin" : "Member";
}

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/** Whole calendar days from `from` to `to` in the viewer's own time zone; negative when earlier. */
function calendarDays(from: Date, to: Date): number {
  const day = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((day(to) - day(from)) / 86_400_000);
}

function shortDate(date: Date, now: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

function plural(count: number, unit: string): string {
  return `${String(count)} ${unit}${count === 1 ? "" : "s"}`;
}

/** "Active now" for the caller, who is by definition here; otherwise how long ago, in days. */
export function lastActiveLabel(at: Date | null, now: Date, you: boolean): string {
  if (you) return "Active now";
  if (!at) return "Not yet active";
  const days = calendarDays(at, now);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return shortDate(at, now);
}

export function requestedLabel(at: Date, now: Date): string {
  const elapsed = now.getTime() - at.getTime();
  if (elapsed < MINUTE) return "Asked just now";
  if (elapsed < HOUR) return `Asked ${plural(Math.floor(elapsed / MINUTE), "minute")} ago`;
  if (elapsed < 24 * HOUR) return `Asked ${plural(Math.floor(elapsed / HOUR), "hour")} ago`;
  const days = calendarDays(at, now);
  return days === 1 ? "Asked yesterday" : `Asked ${shortDate(at, now)}`;
}

export function expiryLabel(invitation: PersonInvitation, now: Date): string {
  if (invitation.status === "expired") return `Expired ${shortDate(invitation.expiresAt, now)}`;
  const remaining = invitation.expiresAt.getTime() - now.getTime();
  if (remaining < HOUR) return "Expires within the hour";
  if (remaining < 24 * HOUR) return `Expires in ${plural(Math.floor(remaining / HOUR), "hour")}`;
  return `Expires in ${plural(Math.floor(remaining / (24 * HOUR)), "day")}`;
}

/** How long ago an invitation went out, for one sent in the last hour; null once it is older news. */
export function sentLabel(invitation: PersonInvitation, now: Date): string | null {
  const elapsed = now.getTime() - invitation.createdAt.getTime();
  if (elapsed >= HOUR) return null;
  if (elapsed < MINUTE) return "Sent just now";
  return `Sent ${plural(Math.floor(elapsed / MINUTE), "minute")} ago`;
}

/** "Maya Chen", "Maya Chen or Tom Ellis", "A, B or C"; more than three end in "another". */
export function whoToAsk(names: readonly string[]): string {
  const shown = names.slice(0, 3);
  const rest = names.length - shown.length;
  const list = rest > 0 ? [...shown, "another owner or admin"] : shown;
  if (list.length <= 1) return list[0] ?? "an owner or admin";
  return `${list.slice(0, -1).join(", ")} or ${list[list.length - 1] ?? ""}`;
}
